import React, { useEffect, useMemo } from "react";
import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

interface AttachedFile {
  id: number;
  file: string;
}

interface FileAttachmentsListProps {
  /** Already-persisted files (empty in the create flow). */
  attachedFiles?: AttachedFile[];
  newFiles: File[];
  themeColor: string;
  /** Only needed when `attachedFiles` can be non-empty (edit flow). */
  onDeleteFile?: (fileId: number) => void;
  onRemoveNewFile: (index: number) => void;
}

const getFileName = (url: string) => {
  try {
    const cleanUrl = url.split("?")[0];
    return decodeURIComponent(cleanUrl.split("/").pop() || "Unnamed file");
  } catch {
    return "Unnamed file";
  }
};

export const FileAttachmentsList: React.FC<FileAttachmentsListProps> = ({
  attachedFiles = [],
  newFiles,
  themeColor,
  onDeleteFile,
  onRemoveNewFile,
}) => {
  const { t } = useTranslation();

  // Local preview URLs so staged files render identically to persisted ones.
  const newFileUrls = useMemo(
    () => newFiles.map((file) => URL.createObjectURL(file)),
    [newFiles]
  );

  useEffect(
    () => () => newFileUrls.forEach((url) => URL.revokeObjectURL(url)),
    [newFileUrls]
  );

  if (attachedFiles.length === 0 && newFiles.length === 0) {
    return null;
  }

  const renderRow = (
    key: string,
    name: string,
    href: string,
    onRemove: () => void,
    backgroundClassName = "bg-gray-50 border"
  ) => (
    <li
      key={key}
      className={`flex items-center justify-between p-2 rounded ${backgroundClassName}`}
    >
      <div className="flex items-center gap-1">
        <span title={name}>{name}</span>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          download={name}
          title={t("taskHub.modal.downloadFile") || ""}
          className="text-blue-500 hover:text-blue-700"
        >
          <DownloadOutlined className="text-xl" />
        </a>
      </div>

      <Button
        style={{ backgroundColor: themeColor }}
        danger
        type="text"
        size="small"
        onClick={onRemove}
      >
        <span className="!p-0">{t("taskHub.modal.delete") || ""}</span>
      </Button>
    </li>
  );

  return (
    <ul className="mt-2 space-y-2">
      {attachedFiles.map((file, idx) =>
        renderRow(`attached-${idx}`, getFileName(file.file), file.file, () =>
          onDeleteFile?.(file.id)
        )
      )}
      {newFiles.map((file, idx) =>
        renderRow(
          `new-${idx}`,
          file.name,
          newFileUrls[idx],
          () => onRemoveNewFile(idx),
          "bg-blue-50 border border-blue-200"
        )
      )}
    </ul>
  );
};
